// flowManifest — the single declaration of what differs per production flow.
//
// AS OF mig 875 this is the OFFLINE / loading / fail-safe FALLBACK layer: the
// authoritative source is the `commodity_templates` DB table (P4 — packhouse #3
// is config, not code). Read it through `useFlowManifest()`
// (src/hooks/useCommodityTemplates.ts), which merges the DB row over this
// in-code copy. The mig-875 seed mirrors the values below EXACTLY, so keep them
// in sync: this is what renders when the DB is unreachable (offline) or a flow
// isn't (yet) in the table, and it's the seed-of-record for CITRUS/BLUEBERRY.
//
// Per flow it documents:
//   - label: display name
//   - flowSpecificStages: production stages that render a FLOW-SPECIFIC
//     component via the stage shells (src/modules/packhouse/stages/*). Every
//     stage NOT listed reuses the shared citrus page unchanged.
//   - curatedModules: the suggested `tenants.enabled_modules` subset for a
//     packhouse of this flow (the actual cap is set per-tenant in the
//     platform-admin app; this is the starting point — refine with the
//     operator). null = uncapped (the full packhouse module set).
//
// Cold-chain age thresholds per flow live in FLOW_AGE_POLICY (src/lib/aging.ts).

import type { ProductionFlow } from '@/lib/useProductionFlow'

export interface FlowManifest {
  label:              string
  flowSpecificStages: string[]
  curatedModules:     string[] | null
  // The commodity name (commodities.name) whose per-commodity registries this
  // flow uses — drives the QC defect filter. null = the default/citrus set
  // (qc_defect_types.commodity_id IS NULL).
  commodityName:      string | null
  // Grade codes shown when the commodity_grades registry (mig 832) has no
  // rows for the context — run without a commodity snapshot, registry still
  // loading, or rows deleted by an admin. Keeps the fallback in the flow's
  // own vocabulary instead of leaking citrus 1/2/P onto berry forms.
  //
  // ⚠️ UPPER CASE, and it must stay that way (mig 20260823000000). Every write
  // of pallets.grade goes through sanitise_pallet_row() -> normalise_code() =
  // upper(trim()), so a title-case fallback ('Class 1') is stored as 'CLASS 1'
  // and then matches nothing in commodity_grades, pallet_marks.grades or any
  // GROUP BY grade. That drift hit 239 of 239 live berry pallets before the
  // registries were normalised; citrus never noticed because 1/2/P are already
  // upper case. Same rule for any grade vocabulary added here later.
  fallbackGrades:     string[]
  // Capability flags — gate citrus-specific certification UI that lives inside
  // SHARED pages (so the page is reused, not forked). These are the in-code
  // prototype of `commodity_templates` capability columns (P4).
  //   usesPPECB:     the PPECB Titan LOI container-consignment flow (Dispatch).
  //                  Blueberry's ex-farm air-freight export uses a different
  //                  process, so this UI is hidden for it (false) in v1.
  //   usesPhytclean: the Phytclean export-whitelist gate on intake (Deliveries).
  //                  Phytclean is the SA citrus pest-free scheme — N/A to berries.
  //   usesForceCooling: the berry force-cool intake stage (crates → dolly →
  //                  force-cool → holding room → release). Citrus tips bins
  //                  straight onto the line, so this is hidden for it (false).
  //   hasTitrationQc: citrus Brix/acid titration QC. Berry maturity is
  //                  firmness+Brix inside BlueberryQC, so titration tabs,
  //                  counts and report columns are hidden when false.
  //   hasPackerQc:   the citrus packer-problem QC lane (hardcoded citrus
  //                  defect list + team leaders). Berry packer issues are
  //                  captured via template checks instead.
  //   tracksPalletWeight: the packhouse puts finished pallets on a scale.
  //                  Citrus bills the grower off net mass, so gross weight is
  //                  required to create a pallet and a weightless one must be
  //                  an awaiting_weight shell. Berry packs to a declared punnet
  //                  fill and sells by carton count — the scale is not in the
  //                  loop at all — so weight is OPTIONAL there and the dispatch
  //                  document drops its mass columns. Enforced server-side by
  //                  pallet_weight_capture_guard (mig 20261020090000), which
  //                  reads the DB column of the same name.
  //   usesReturnableBinLoop: the KF49 returnable-bin / empties loop — the
  //                  empties-out section + "Uses KF49 bins" + drench intake_path
  //                  on DeliveryDetail. Berry runs one-way crates (no empties
  //                  loop, no drench), so this citrus UI is hidden (false).
  //                  This is the in-code prototype of the Phase-C
  //                  `commodity_templates.uses_returnable_bin_loop` column
  //                  (see docs/COMMODITY-FLOW-STRATEGY.md §4). Read via the DB
  //                  manifest with an in-code fallback (useCommodityTemplates).
  usesPPECB:          boolean
  usesPhytclean:      boolean
  usesForceCooling:   boolean
  hasTitrationQc:     boolean
  hasPackerQc:        boolean
  usesReturnableBinLoop: boolean
  tracksPalletWeight:    boolean
}

export const FLOW_MANIFEST: Record<ProductionFlow, FlowManifest> = {
  CITRUS: {
    label:              'Citrus',
    flowSpecificStages: [],          // every stage uses the existing shared page
    curatedModules:     null,        // uncapped — the full packhouse module set
    commodityName:      null,        // default/citrus defect set (commodity_id IS NULL)
    fallbackGrades:     ['1', '2', 'P'],
    usesPPECB:          true,        // PPECB Titan LOI container flow (the citrus default)
    usesPhytclean:      true,        // Phytclean export whitelist on intake
    usesForceCooling:   false,       // citrus tips bins straight onto the line — no precool stage
    hasTitrationQc:     true,        // Brix/acid titration is core citrus QC
    hasPackerQc:        true,        // packer-problem lane with the citrus defect list
    usesReturnableBinLoop: true,     // KF49 returnable-bin / empties loop + drench intake_path
    tracksPalletWeight: true,        // grower is billed off net mass — every pallet is weighed
  },
  BLUEBERRY: {
    label:              'Blueberry',
    commodityName:      'BLUEBERRIES',
    fallbackGrades:     ['CLASS 1', 'CLASS 2', 'JUICE', 'REJECT'],
    usesPPECB:          false,       // air-freight export, not PPECB container LOI
    usesPhytclean:      false,       // Phytclean is citrus-specific
    usesForceCooling:   true,        // crates → dolly → force-cool → holding → release
    hasTitrationQc:     false,       // berry maturity = firmness+Brix in the berry Full Box form
    hasPackerQc:        false,       // no citrus packer lane — use template checks
    usesReturnableBinLoop: false,    // one-way crates — no empties loop, no drench intake_path
    tracksPalletWeight: false,       // punnets are packed to a declared fill; pallets are sold by count, never weighed
    // 'qc' renders BlueberryQC (berry defects/firmness, no citrus titration).
    // 'deliveries' renders BlueberryDeliveries (crate intake — no drench
    // pipeline, no KF49 empties loop, no Phytclean; feeds Precool).
    // 'pallets' pack-types/regimes are flow-aware params on the shared page
    // (P2-tail), not a separate component. Inspection is handled by OMITTING
    // the `inspection` module below (blueberry has no PPECB), so no shell.
    flowSpecificStages: ['qc', 'deliveries'],
    // Starting suggestion — omits `inspection` (no PPECB for blueberry). Tune
    // with the operator during tenant standup (P3).
    curatedModules: [
      'deliveries', 'receipts', 'bin_inventory', 'precool',
      'tipping', 'runs', 'pallets', 'rebin', 'rejects', 'reject_bins',
      'coldroom', 'loading_bay', 'yard',
      'qc', 'compliance', 'my_checklists',
      'consignments', 'dispatch', 'reject_dispatch',
      'growers', 'packers', 'labour', 'clock', 'financial', 'estimates',
      'activity', 'monitor', 'maintenance', 'stock',
      'users', 'config', 'commodities',
      // Piece work + teams (mig 20261051800000, F11 D5): the packhouses this
      // flow serves pack by hand with no line system, so a new one must be able
      // to open Piece Work Capture. Appended last, in this order, as there.
      'packer_piecework', 'teams', 'team_performance',
    ],
  },
  // STRAWBERRY runs the same force-cooled crate pipeline as blueberry — the
  // fruit differs, the process does not. Values copied from the PROD
  // commodity_templates row (label 'Strawberry', commodity_name
  // 'STRAWBERRIES', sort_order 30), not invented.
  //
  // WHY THIS ENTRY EXISTS AT ALL, given the DB template is authoritative:
  // resolveManifest() is `templates?.[key] ?? FLOW_MANIFEST[key] ?? CITRUS`.
  // With no in-code STRAWBERRY, a tenant that runs the lane resolved to CITRUS
  // whenever the templates query had not landed — wrong varieties, wrong intake
  // sizes, and usesForceCooling FALSE on fruit that must be force-cooled. That
  // was unreachable while a tenant could only ever resolve its own default
  // flow; the berry-intake commodity picker (#1826) made a non-default key
  // selectable, so the fallback has to be truthful for every lane we run.
  STRAWBERRY: {
    label:              'Strawberry',
    commodityName:      'STRAWBERRIES',
    fallbackGrades:     ['CLASS 1', 'CLASS 2', 'JUICE', 'REJECT'],
    usesPPECB:          false,
    usesPhytclean:      false,
    usesForceCooling:   true,        // the safety-critical one — see above
    hasTitrationQc:     false,
    hasPackerQc:        false,
    usesReturnableBinLoop: false,
    tracksPalletWeight: false,       // same as blueberry — no scale in the pack-out loop
    flowSpecificStages: ['qc', 'deliveries'],
    curatedModules: [
      'deliveries', 'receipts', 'bin_inventory', 'precool',
      'tipping', 'runs', 'pallets', 'rebin', 'rejects', 'reject_bins',
      'coldroom', 'loading_bay', 'yard',
      'qc', 'compliance', 'my_checklists',
      'consignments', 'dispatch', 'reject_dispatch',
      'growers', 'packers', 'labour', 'clock', 'financial', 'estimates',
      'activity', 'monitor', 'maintenance', 'stock',
      'users', 'config', 'commodities',
      'packer_piecework', 'teams', 'team_performance',   // mig 20261051800000 — as BLUEBERRY
    ],
  },
}

// ---------------------------------------------------------------------------
// Pipeline overlay (P2a) — the in-code FALLBACK for commodity_template_stages
// (mig 879), read via useFlowPipeline() in src/hooks/useCommodityTemplates.ts.
//
// A SPARSE overlay on the hasModule-driven dashboard: `hidden` lists process
// keys (MODULES[].key) to drop for the flow; `order` optionally positions a
// process WITHIN its category band. A key absent from both = today's behaviour.
// This BYTE-MIRRORS the mig-879 seed so behaviour is identical offline / before
// the migration is applied. CITRUS hides 'precool' (the old in-code
// BLUEBERRY_ONLY_MODULES subtraction); BLUEBERRY shows it.
// ---------------------------------------------------------------------------
export interface FlowPipelineOverlay {
  hidden: string[]
  order?: Record<string, number>
}

export const FLOW_PIPELINE_OVERLAY: Record<ProductionFlow, FlowPipelineOverlay> = {
  CITRUS:     { hidden: ['precool'] },
  BLUEBERRY:  { hidden: [] },
  STRAWBERRY: { hidden: [] },   // force-cooled, same as blueberry — precool shows
}
